import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import { ApolloProvider } from '@apollo/client';
import Script from 'next/script';
import { useRouter } from 'next/router';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Box } from '@mui/material';
import theme from '../theme/theme';
import createEmotionCache from '../../util/EmotionCache';
// import EirHeader from '../components/eir-header/EirHeader';
// import EirFooter from '../components/eir-footer/EirFooter';
import './global.css';
import client from '../../util/GraphqlClient';
import RootContext, { defaultStates, reducer } from '../../util/root.context';
import EirCartContainer from '../components/eir-cart/EirCartContainer';
import { GTM_ID, pageview } from '../../util/gtm';
import { CookieProAccessibility } from '../components/shared/CookieProScripts';
// import Matomo from '../components/shared/Matomo';
import { getCookie } from '../hooks/useCookie';
import Session from '../components/shared/eir-session/Session';
import Genesys from '../components/shared/Genesys';
import { RECAPTCHA_KEY_V3 } from '../components/shared/Constants';
import Qualtrics from '../components/shared/Qualtrics';
import useWebChatFocusOrder from '../hooks/webchatFocusOrder';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();
let isTabPressed = false;

const MyApp = props => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;
  const [state, dispatch] = React.useReducer(reducer, defaultStates);
  const [loggedIn, setLoggedIn] = React.useState(false);
  const router = useRouter();

  // Focus webchat on TAB
  useWebChatFocusOrder();

  useEffect(() => {
    // Update GTM on every page visit
    pageview(router.asPath);
  }, [router.asPath]);

  useEffect(() => {
    const loggedInCustomer = getCookie('customer_Name');

    if (loggedInCustomer) {
      setLoggedIn(true);
      dispatch({ type: 'ENABLE_LOGOUT', payload: true });
    }
  }, []);

  useEffect(() => {
    setLoggedIn(true);
  }, [state.enableLogout]);

  useEffect(() => {
    function handleFocus(e) {
      const el = e.target;

      let controlWrapper = null;
      if (el.type === 'checkbox' || el.type === 'radio') {
        controlWrapper = el.closest('.MuiButtonBase-root');
      }

      if (!isTabPressed) return;

      if (el.getAttribute('role') === 'radiogroup') return;

      // const textFieldContainer = el.closest('.MuiFormControl-root');
      const textFieldContainer =
        el.type === 'radio' || el.type === 'checkbox'
          ? null
          : el.closest('.MuiInputBase-root') || el.parentElement?.closest('.MuiInputBase-root');
      const targetElement =
        controlWrapper || (el.type !== 'radio' ? textFieldContainer : null) || el;

      const getClosestBackgroundColor = element => {
        let current = element.parentElement;
        let solidColor = null;
        while (current && current !== document) {
          const bgImage = window.getComputedStyle(current).backgroundImage;
          if (bgImage && bgImage !== 'none') {
            return { color: 'transparent', isSolid: false };
          }
          const bgColor = window.getComputedStyle(current).backgroundColor;
          if (bgColor && !bgColor.includes('rgba(0, 0, 0, 0)') && bgColor !== 'transparent') {
            if (!solidColor) solidColor = bgColor;
          }
          current = current.parentElement;
        }
        return solidColor
          ? { color: solidColor, isSolid: true }
          : { color: '#000000', isSolid: true };
      };

      // const { color: bg, isSolid } = getClosestBackgroundColor(targetElement);
      const forcedBg = targetElement.closest('[data-focus-bg]');
      const { color: bg, isSolid } = forcedBg
        ? { color: forcedBg.getAttribute('data-focus-bg'), isSolid: true }
        : getClosestBackgroundColor(targetElement);

      if (controlWrapper && (el.type === 'checkbox' || el.type === 'radio')) {
        controlWrapper.style.display = 'inline-flex';
        controlWrapper.style.padding = '0px';
        controlWrapper.style.margin = '9px';
        controlWrapper.style.overflow = 'hidden';
        const ripple = controlWrapper.querySelector('.MuiTouchRipple-root');
        if (ripple) ripple.style.display = 'none';
      }

      targetElement.style.outline = 'none';

      // Fix for inline elements
      if (window.getComputedStyle(targetElement).display === 'inline') {
        targetElement.style.display = 'inline-block';
      }

      if (!isSolid) {
        // On background image — double ring works on any bg
        targetElement.style.boxShadow = `
      0 0 0 3px #493553,
      0 0 0 5px #FFFFFF,
      0 0 0 7px #493553
    `;
      } else {
        // On solid background — use bg color as gap
        targetElement.style.boxShadow = `
      0 0 0 3px ${bg},
      0 0 0 5px #FFFFFF,
      0 0 0 7px #493553
    `;
      }

      targetElement.style.borderRadius = '3px';
    }

    function handleBlur(e) {
      const el = e.target;
      if (el.getAttribute('role') === 'radiogroup') return;

      let controlWrapper = null;
      if (el.type === 'checkbox' || el.type === 'radio') {
        controlWrapper = el.closest('.MuiButtonBase-root');
      }

      const textFieldContainer =
        el.type === 'radio' || el.type === 'checkbox'
          ? null
          : el.closest('.MuiInputBase-root') || el.parentElement?.closest('.MuiInputBase-root');

      const targetElement =
        controlWrapper || (el.type !== 'radio' ? textFieldContainer : null) || el;

      if (textFieldContainer && el.type !== 'radio') {
        targetElement.style.outline = '';
        targetElement.style.outlineOffset = '';
        targetElement.style.boxShadow = '';
        targetElement.style.borderRadius = '';
        targetElement.style.borderColor = '';
        return;
      }

      if (controlWrapper) {
        controlWrapper.style.padding = '';
        controlWrapper.style.margin = '';
      }

      targetElement.style.outline = '';
      targetElement.style.outlineOffset = '';
      targetElement.style.boxShadow = '';
      targetElement.style.borderRadius = '';
    }

    document.addEventListener('keydown', e => {
      if (e.key === 'Tab') {
        isTabPressed = true;
      }
    });

    document.addEventListener('mousedown', () => {
      isTabPressed = false;
    });

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('keydown', () => {});
      document.removeEventListener('mousedown', () => {});

      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
    };
  }, []);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_KEY_V3}>
      <RootContext.Provider value={{ state, dispatch }}>
        <CacheProvider value={emotionCache}>
          {/* Google Tag Manager - Global base code */}
          <Script
            id='gtag-base'
            strategy='beforeInteractive'
            dangerouslySetInnerHTML={{
              __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer', '${GTM_ID}');
          `,
            }}
          />
          {/* <Matomo /> */}
          <CookieProAccessibility />
          <Qualtrics />
          <Genesys />
          <Head>
            <meta name='viewport' content='initial-scale=1, width=device-width' />
            <title>Eir | Home</title>
          </Head>
          <ApolloProvider client={client}>
            <ThemeProvider theme={theme}>
              {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
              <CssBaseline />

              <main>
                {/* <EirHeader /> */}
                <Box sx={{ pb: 2 }}>
                  {loggedIn && <Session />}
                  <Component {...pageProps} />
                </Box>

                <EirCartContainer />
              </main>
              {/* <EirFooter /> */}
            </ThemeProvider>
          </ApolloProvider>
        </CacheProvider>
      </RootContext.Provider>
    </GoogleReCaptchaProvider>
  );
};

MyApp.propTypes = {
  Component: PropTypes.elementType.isRequired,
  emotionCache: PropTypes.object,
  pageProps: PropTypes.object.isRequired,
};

MyApp.defaultProps = {
  emotionCache: clientSideEmotionCache,
};

export default MyApp;
